import LockResetIcon from "@mui/icons-material/LockReset";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/Person";
import Refresh from "@mui/icons-material/Refresh";
import SettingsIcon from "@mui/icons-material/Settings";
import { Box as UserInfoWrapper, Divider, Tooltip } from "@mui/material";
import ListItemIcon from "@mui/material/ListItemIcon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import {
  OrganizationContext,
  UserDirectoryContext,
  useTranslation,
} from "hhn-commons";
import { useSnackbar } from "notistack";
import React, { useContext } from "react";
import { Link } from "react-router";
import DisplayAvatarImage from "../../common/DisplayAvatarImage";
import { RightDrawerContext } from "../../context/RightDrawerContext";
import { MODAL_ROUTES } from "../../routes";
import { AVATAR_SIZE } from "../../utils/AvatarUtils";
import UserSettings from "../userSettings/UserSettings";
import {
  AvatarIconContainer,
  PaperProp,
  UserDesignation,
  UserName,
} from "./UserMenu.style";

interface UserMenuProps {
  onLogout: () => void;
}

export const UserMenu = ({ onLogout }: UserMenuProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "organization" });
  const tNsChangePassword = (key: string) => t(key, { ns: "changePassword" });
  const { selectedOrgUser } = useContext(OrganizationContext);
  const { userDirectory } = useContext(UserDirectoryContext);
  const { openDrawer, setContent } = useContext(RightDrawerContext);
  const currentOrganization = selectedOrgUser?.getOrganization();
  const userProfile = currentOrganization?.userProfile;
  const userInfo = userProfile
    ? (userDirectory?.getUserInfo(userProfile.id) ?? userProfile)
    : undefined;
  const userName = userInfo?.name ?? "";
  const userDesignation = userInfo?.designation?.name;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const openSettings = () => {
    setContent(<UserSettings />);
    openDrawer();
  };

  const onRefresh = () => {
    enqueueSnackbar(t("notImplemented"), { autoHideDuration: 3000 });
  };

  return (
    <React.Fragment>
      <Tooltip title="Profile settings and more" placement="right-end">
        <AvatarIconContainer
          onClick={handleClick}
          size="small"
          aria-controls={open ? "account-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
        >
          <DisplayAvatarImage
            avatarSize={AVATAR_SIZE.MEDIUM}
            alt={userInfo?.firstName ?? ""}
            src={userInfo?.image ?? ""}
          />
        </AvatarIconContainer>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        slotProps={{
          paper: {
            elevation: 0,
            sx: PaperProp,
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem>
          <DisplayAvatarImage
            avatarSize={AVATAR_SIZE.LARGE}
            alt={userInfo?.firstName ?? ""}
            src={userInfo?.image ?? ""}
          />
          <UserInfoWrapper>
            <UserName data-testid="userName">{userName}</UserName>
            {userDesignation && (
              <UserDesignation data-testid="UserDesignation">
                {userDesignation}
              </UserDesignation>
            )}
          </UserInfoWrapper>
        </MenuItem>
        <Divider />
        <MenuItem
          data-testid="user-profile"
          component={Link}
          to={`?modal=${MODAL_ROUTES.USER_PROFILE}`}
        >
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          {tNs("menuButton.profile")}
        </MenuItem>
        <MenuItem onClick={onRefresh} disabled>
          <ListItemIcon>
            <Refresh fontSize="small" />
          </ListItemIcon>
          {tNs("menuButton.refresh")}
        </MenuItem>
        <MenuItem onClick={openSettings}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          {tNs("menuButton.settings")}
        </MenuItem>
        <MenuItem
          data-testid="change-password"
          component={Link}
          to={`?modal=${MODAL_ROUTES.CHANGE_PASSWORD}`}
        >
          <ListItemIcon>
            <LockResetIcon fontSize="small" />
          </ListItemIcon>
          {tNsChangePassword("changePassword")}
        </MenuItem>
        <Divider />
        <MenuItem onClick={onLogout} data-testid="logoutButton">
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          {t("logout")}
        </MenuItem>
      </Menu>
    </React.Fragment>
  );
};
